import React, { useState, useRef, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth';

const TopHeader = ({ user, onLogout, isSidebarCollapsed, toggleSidebar }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await authService.logout();
      if (onLogout) onLogout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Parse path for breadcrumbs
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const breadcrumb = pathSegments.length > 0
    ? pathSegments[0].charAt(0).toUpperCase() + pathSegments[0].slice(1)
    : 'Dashboard';

  const subBreadcrumb = pathSegments.length > 1
    ? pathSegments[1].charAt(0).toUpperCase() + pathSegments[1].slice(1)
    : '';

  return (
    <div className="flex items-center justify-between px-4 sm:px-8 py-4 sticky top-0 z-40 transition-all duration-300" style={{ backgroundColor: '#0a0f1e', borderBottom: '1px solid #1e2a47' }}>
      {/* Left Section: Toggle & Breadcrumb */}
      <div className="flex items-center gap-3 sm:gap-4">
        <button
          onClick={toggleSidebar}
          className="p-2 rounded-lg text-[#94a3b8] hover:bg-white/5 hover:text-white transition-colors lg:hidden"
        >
          <i className="bi bi-list text-2xl"></i>
        </button>

        <div className="hidden sm:flex items-center gap-2">
          <span className="text-white font-bold text-sm">
            {breadcrumb}
            {subBreadcrumb && <span className="mx-2" style={{ color: '#64748b' }}>›</span>}
            {subBreadcrumb && <span style={{ color: '#64748b' }}>{subBreadcrumb}</span>}
          </span>
        </div>
      </div>

      {/* Right Actions */}
      <div className="flex items-center gap-4">
        {/* Search */}
        <div className="relative hidden md:block">
          <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#64748b', fontSize: '14px' }}></i>
          <input
            type="text"
            className="pl-10 pr-4 py-2 rounded-lg text-sm font-medium outline-none transition-all"
            placeholder="Global Search..."
            style={{
              backgroundColor: '#161e35',
              color: '#f8fafc',
              border: 'none',
              width: '300px'
            }}
          />
        </div>

        {/* Notifications */}
        <div className="relative cursor-pointer">
          <i className="bi bi-bell text-lg transition-colors" style={{ color: '#94a3b8' }}></i>
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
        </div>

        {/* Profile Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <div
            className="flex items-center gap-3 cursor-pointer px-3 py-2 rounded-xl transition-all"
            onClick={() => setShowDropdown(!showDropdown)}
            style={{
              backgroundColor: showDropdown ? 'rgba(255,255,255,0.05)' : 'transparent',
              userSelect: 'none'
            }}
          >
            <div
              className="flex items-center justify-center font-bold text-white rounded-full"
              style={{
                width: '36px',
                height: '36px',
                background: '#ef4444',
              }}
            >
              {user?.first_name ? user.first_name.charAt(0).toUpperCase() : 'A'}
            </div>
            <div className="hidden lg:block">
              <div className="text-white font-bold text-sm leading-tight">
                {user?.first_name || 'Admin'}
              </div>
              <div className="text-xs" style={{ color: '#64748b' }}>
                {user?.email || 'Whatnew'}
              </div>
            </div>
            <i className={`bi bi-chevron-down transition-transform text-xs ${showDropdown ? 'rotate-180' : ''}`} style={{ color: '#94a3b8' }}></i>
          </div>

          {/* Popup Menu */}
          {showDropdown && (
            <div
              className="absolute right-0 mt-2 p-2 rounded-2xl shadow-2xl"
              style={{
                width: '280px',
                backgroundColor: '#161e35',
                border: 'none',
                zIndex: 1050
              }}
            >
              <div className="p-3 mb-2" style={{ borderBottom: 'none' }}>
                <p className="text-white font-bold mb-1 text-sm">{user?.first_name || 'Admin'} {user?.last_name || ''}</p>
                <p className="text-xs mb-2 truncate" style={{ color: '#94a3b8' }}>{user?.email}</p>
                <div className="mt-2">
                  <span className="px-2 py-1 rounded-lg text-xs font-bold" style={{
                    backgroundColor: user?.profile?.verification_status === 'verified' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                    color: user?.profile?.verification_status === 'verified' ? '#22c55e' : '#ef4444'
                  }}>
                    {user?.profile?.verification_status === 'verified' ? 'Verified Seller' : 'Seller Account'}
                  </span>
                </div>
              </div>

              <ul className="list-none p-0 m-0">
                <li>
                  <Link to="/profile" className="flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm font-medium no-underline" style={{ color: '#94a3b8' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.color = '#ffffff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#94a3b8';
                    }}
                  >
                    <i className="bi bi-person-gear"></i>
                    <span>Profile Settings</span>
                  </Link>
                </li>
                <li>
                  <Link to="/credits" className="flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm font-medium no-underline" style={{ color: '#94a3b8' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.color = '#ffffff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#94a3b8';
                    }}
                  >
                    <i className="bi bi-coin"></i>
                    <span>Credits & Billing</span>
                  </Link>
                </li>
                <li>
                  <Link to="/profile/notifications" className="flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm font-medium no-underline" style={{ color: '#94a3b8' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.color = '#ffffff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#94a3b8';
                    }}
                  >
                    <i className="bi bi-bell"></i>
                    <span>Notifications</span>
                  </Link>
                </li>
                <li>
                  <hr className="my-2" style={{ borderColor: 'transparent', opacity: 1 }} />
                </li>
                <li>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 px-3 py-2 w-full border-0 bg-transparent rounded-lg transition-all text-sm font-medium"
                    style={{ color: '#ef4444' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <i className="bi bi-box-arrow-right"></i>
                    <span>Sign Out</span>
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopHeader;

